import { useState } from "react";
import { Upload, X, CheckCircle } from "lucide-react";
import ErrorRowsModal from "../defective-row-extractor/DRX";

interface CSVUploaderProps {
  onUpload: (formdata: FormData) => void;
  setFile: (file: File) => void;
  file: File | null;
}

export default function CSVUploader({
  onUpload,
  setFile,
  file,
}: CSVUploaderProps) {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);
  };

  const validateFile = (file: File | null): boolean => {
    if (!file) return false;

    const validTypes: string[] = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    const validExtensions: string[] = [".csv", ".xlsx", ".xls"];
    const hasValidType: boolean = validTypes.includes(file.type);
    const hasValidExtension: boolean = validExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );

    if (!hasValidType && !hasValidExtension) {
      setError("Please upload a CSV or Excel file (.csv, .xlsx, .xls)");
      return false;
    }

    const maxSize: number = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError("File size must be less than 10MB");
      return false;
    }

    setError("");
    return true;
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);
    const formdata = new FormData();
    const droppedFile = e.dataTransfer.files[0];
    if (validateFile(droppedFile)) {
      formdata.append("file", droppedFile);
      onUpload(formdata);
      setFile(droppedFile);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = e.target.files?.[0];
    const formdata = new FormData();
    if (selectedFile && validateFile(selectedFile)) {
      formdata.append("file", selectedFile);
      onUpload(formdata);
      setFile(selectedFile);
    }
  };

  // const removeFile = (): void => {
  //   setFile(null);
  //   setError("");
  // };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes: string[] = ["Bytes", "KB", "MB"];
    const i: number = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <div className="min-h-fit  flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl  p-8 w-full max-w-md">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          Upload CSV or Excel File
        </h2>

        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
            isDragging
              ? "border-indigo-500 bg-indigo-50"
              : "border-gray-300 hover:border-indigo-400 hover:bg-gray-50"
          }`}
        >
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileInput}
            className="hidden"
            id="csv-input"
          />

          <label htmlFor="csv-input" className="cursor-pointer">
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600 mb-2">Drag and drop your file here</p>
            <p className="text-sm text-gray-500 mb-4">or</p>
            <span className="inline-block px-6 py-2 bg-primary text-white rounded-lg hover:bg-[#081f74] transition-colors">
              Browse Files
            </span>
          </label>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* {file && !error && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>
              <button
                onClick={removeFile}
                className="ml-2 text-gray-400 hover:text-red-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )} */}

        {/* {file && !error && (
          <button className="w-full mt-6 px-6 py-3 bg-primary text-white rounded-lg hover:bg-[#081f74] transition-colors font-medium">
            Process File
          </button>
        )} */}

        <p className="text-xs text-gray-500 text-center mt-6">
          Maximum file size: 10MB
        </p>
      </div>
    </div>
  );
}
